import { quoteApi } from "@/main/api";
import { QuoteSelectBestSupplierRequest } from "@/main/models/request/quoteRequest";
import { QuoteGetItemConditionsWrapper } from "@/main/models/response";

import { IDamagePart } from "../../damageParts/types";
import { IPartOffer } from "../types";
import {
	buildOfferFallbackFromPart,
	buildUnavailableOffer,
	getDeadlineAnswerFromItems,
	mapBestSupplierToOffer,
	mapQuoteItemsToOffers,
} from "../utilsSummary";
import { saveSupplierOnCart } from "./supplierOffersService";

const CONCURRENCY = 4;

async function saveOfferOnCart(guidQuote: string, offer: IPartOffer): Promise<IPartOffer> {
	try {
		const savedOffer = await saveSupplierOnCart(guidQuote, offer);
		return savedOffer ?? buildUnavailableOffer(offer);
	} catch {
		return buildUnavailableOffer(offer);
	}
}

async function fetchOfferByPart(guidQuote: string, part: IDamagePart): Promise<IPartOffer> {
	const request: QuoteSelectBestSupplierRequest = {
		quoteGuid: guidQuote,
		partGuid: part.guidPart,
		findMoreSuppliers: false,
		automaticQuote: false,
	};

	try {
		const { data } = await quoteApi.getSelectBestSupplier(request, {
			suppressToast: true,
		});
		const offer = mapBestSupplierToOffer(part, data.result);

		if (!offer.isAvailable) return offer;

		return await saveOfferOnCart(guidQuote, offer);
	} catch {
		return buildUnavailableOffer(buildOfferFallbackFromPart(part));
	}
}

export async function fetchQuoteItemConditions(guidQuote: string): Promise<QuoteGetItemConditionsWrapper> {
	const { data } = await quoteApi.getItems({ quoteGuid: guidQuote });

	return data.result;
}

export async function fetchOffersSavedOnQuote(guidQuote: string): Promise<IPartOffer[]> {
	return mapQuoteItemsToOffers(await fetchQuoteItemConditions(guidQuote));
}

export async function fetchDeadlineAnswer(guidQuote: string): Promise<string> {
	return getDeadlineAnswerFromItems(await fetchQuoteItemConditions(guidQuote));
}

function groupPartsByGuidPart(parts: IDamagePart[]): IDamagePart[][] {
	const groups = new Map<string, IDamagePart[]>();

	parts.forEach((part) => {
		groups.set(part.guidPart, [...(groups.get(part.guidPart) ?? []), part]);
	});

	return Array.from(groups.values());
}

function fetchOffersByGroup(guidQuote: string, group: IDamagePart[]): Promise<IPartOffer[]> {
	return group.reduce<Promise<IPartOffer[]>>(async (previous, part) => {
		const offers = await previous;
		return [...offers, await fetchOfferByPart(guidQuote, part)];
	}, Promise.resolve([]));
}

export function fetchBestOffers(guidQuote: string, parts: IDamagePart[]): Promise<IPartOffer[]> {
	const groups = groupPartsByGuidPart(parts);
	const batches: IDamagePart[][][] = [];
	for (let index = 0; index < groups.length; index += CONCURRENCY) {
		batches.push(groups.slice(index, index + CONCURRENCY));
	}

	return batches.reduce<Promise<IPartOffer[]>>(async (previous, batch) => {
		const offers = await previous;
		const results = await Promise.all(batch.map((group) => fetchOffersByGroup(guidQuote, group)));
		return [...offers, ...results.flat()];
	}, Promise.resolve([]));
}
